<template>
  <!-- Vazio → o bloco NÃO existe (M1, balão ④). Um acordeão "Histórico de fontes
       (0 superadas)" seria ruído em todo plantão resolvido de primeira, que é a
       maioria. -->
  <CollapsibleSection
    v-if="items.length"
    :title="title"
    :test-id="testId"
    :default-expanded="false"
  >
    <ul class="flex flex-col gap-1.5 list-none p-0 m-0" :data-testid="`${testId}-list`">
      <li
        v-for="(item, index) in items"
        :key="`${item.source}-${index}`"
        class="flex items-start gap-2 rounded border border-gray-30 bg-gray-50 px-2 py-1.5 text-xs text-left"
        data-testid="station-source-history-item"
      >
        <span class="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 font-bold text-gray-600">{{
          rankLabel(item)
        }}</span>
        <span class="min-w-0 break-words">
          <span class="font-semibold">{{ sourceLabel(item) }}</span>
          <span v-if="timeLabel(item)" class="ml-1 font-semibold">{{ timeLabel(item) }}</span>
          <!-- Veredito em elemento próprio: "superada" e "descartada" são estados
               DIFERENTES (balão ④ + ponto em aberto do M1) e o motivo em texto
               livre do backend não pode ser o único a distingui-los. -->
          <span
            class="ml-1 rounded px-1 py-0.5 font-semibold uppercase"
            :class="outcomeClass(item)"
            data-testid="station-source-history-outcome"
            >{{ outcomeLabel(item) }}</span
          >
          <span class="text-gray-500"> — {{ reasonLabel(item) }}</span>
        </span>
      </li>
    </ul>
  </CollapsibleSection>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import CollapsibleSection from './CollapsibleSection.vue'
import { EFFECTIVE_SOURCE_KIND_LABELS } from '@/constants/ds-attendance.constants'
import { formatHourMinuteInOffset } from '@/utils/date'
import type {
  StationRegistrationSourceOutcome,
  StationRegistrationSupersededSource
} from '@/Network/Services/StationService'

/**
 * Veredito de uma fonte que NÃO venceu o ranking daquela perna.
 *
 * O contrato mora no `StationService` (é `GET /stations/:id/registration` quem o
 * devolve); aqui fica o apelido local. Os campos opcionais são de propósito: a
 * F1 grava um histórico mais pobre — sem `rank`/`outcome` a linha continua
 * legível ("— superada no ranking"), que é melhor do que esconder a fonte.
 */
export type SupersededSource = StationRegistrationSupersededSource

const props = withDefaults(
  defineProps<{
    sources: SupersededSource[]
    /**
     * Base dos `data-testid`. Tem default, mas as duas pernas do Registro de
     * Plantão precisam de valores distintos — dois `-header` iguais na mesma
     * árvore quebrariam qualquer busca por testid.
     */
    testId?: string
    /** Offset ISO do plantão (`-03:00`). Nome IANA cai em Brasília — ver `utils/date`. */
    timezone?: string
  }>(),
  { testId: 'station-source-history-accordion', timezone: '-03:00' }
)

/**
 * `WON` é a fonte VENCEDORA — ela já aparece em destaque no corpo da perna. O
 * histórico cru do backend (`history[]`) traz as três caixas juntas, então o
 * filtro fica aqui: quem monta a lista pode passar o histórico inteiro sem
 * precisar conhecer o vocabulário de vereditos.
 */
const items = computed(() => props.sources.filter((item) => item.outcome !== 'WON'))

const countOf = (outcome: StationRegistrationSourceOutcome) =>
  items.value.filter((item) => (item.outcome ?? 'SUPERSEDED') === outcome).length

const title = computed(() => {
  const superseded = countOf('SUPERSEDED') + countOf('NO_RECORD')
  const discarded = countOf('DISCARDED')
  const parts: string[] = []
  if (superseded) parts.push(`${superseded} ${superseded === 1 ? 'superada' : 'superadas'}`)
  if (discarded) parts.push(`${discarded} ${discarded === 1 ? 'descartada' : 'descartadas'}`)
  return `Histórico de fontes (${parts.join(' · ')})`
})

const OUTCOME_REASONS: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'venceu o ranking',
  SUPERSEDED: 'superada no ranking',
  DISCARDED: 'descartada',
  NO_RECORD: 'sem registro na perna'
}

const OUTCOME_LABELS: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'venceu',
  SUPERSEDED: 'superada',
  DISCARDED: 'descartada',
  NO_RECORD: 'sem registro'
}

const OUTCOME_CLASSES: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'bg-green-50 text-sucess',
  SUPERSEDED: 'bg-yellow-50 text-warning',
  DISCARDED: 'bg-red-50 text-negative',
  NO_RECORD: 'bg-gray-200 text-gray-600'
}

const outcomeOf = (item: SupersededSource): NonNullable<SupersededSource['outcome']> =>
  item.outcome ?? 'SUPERSEDED'

const rankLabel = (item: SupersededSource): string => {
  if (item.rank == null) return '—'
  // `99` é o código da F1 para "fora do ranking configurado na escala".
  return item.rank === 99 ? '#—' : `#${item.rank}`
}

const sourceLabel = (item: SupersededSource): string =>
  item.label || EFFECTIVE_SOURCE_KIND_LABELS[item.source] || item.source

const timeLabel = (item: SupersededSource): string | null =>
  item.value ? formatHourMinuteInOffset(item.value, props.timezone) : null

const outcomeLabel = (item: SupersededSource): string => OUTCOME_LABELS[outcomeOf(item)]

const outcomeClass = (item: SupersededSource): string => OUTCOME_CLASSES[outcomeOf(item)]

const reasonLabel = (item: SupersededSource): string =>
  item.reason || OUTCOME_REASONS[outcomeOf(item)]
</script>
